import { inject, Injectable } from '@angular/core';
import {
  BattleListsBackendService,
  CasesBackendService,
  ChallengeBackendService,
  ClassicBackendService,
  HiloBackendService,
} from '@dev-fast/backend-services';
import type {
  ICaseCollection,
  ICaseItemDtoV2,
  IClassicStateDto,
  IHiloCurrentGameState,
  IHiloHistoryItem,
  IUserDetailed,
} from '@dev-fast/types';
import { APP_PATHS, DEFAULT_CHALLENGE_CASES_LIST_FILTERS, EWalletType } from '@dev-fast/types';
import type { Observable } from 'rxjs';
import { BehaviorSubject, forkJoin, map, merge, of, switchMap, timer } from 'rxjs';

import { EnvironmentService } from '@app/core/environment-service';

import type { ILoadResolverParams } from '../types/types';

@Injectable({
  providedIn: 'root',
})
export class LoadingService {
  readonly #casesBackendService = inject(CasesBackendService);
  readonly #hiloBackendService = inject(HiloBackendService);
  readonly #challengeBackendService = inject(ChallengeBackendService);
  readonly #battleListsBackendService = inject(BattleListsBackendService);
  readonly #classicBackendService = inject(ClassicBackendService);
  readonly #environmentService = inject(EnvironmentService);

  readonly #loadingProgressSubject = new BehaviorSubject<number | undefined>(undefined);

  readonly loadingProgress$: Observable<number | undefined> = this.#loadingProgressSubject.pipe(
    switchMap((progress) =>
      progress === 100
        ? merge(of(100), timer(500).pipe(switchMap(() => of(undefined)))) // Через 0.5 сек. сбрасываем
        : of(progress),
    ),
  );

  setLoadingProgress(progress?: number): void {
    this.#loadingProgressSubject.next(progress);
  }

  /** Метод для предзагрузке при переходе по роуту. Возвращает данные в зависимости от роута */
  getFirstLoadDataByRoute({ route, user }: ILoadResolverParams): Observable<any> {
    const routePath = route.routeConfig?.path;
    const gameKey = routePath ? this.#environmentService.getGameModuleKey(routePath) : undefined;

    switch (gameKey) {
      case APP_PATHS.games.cases.base:
        return this.#getCasesFirstLoad();
      case APP_PATHS.games.vaults.base:
        return this.#getCasesFirstLoad();
      case APP_PATHS.games.hilo.base:
        return this.#getHiloFirstLoad(user, route.parent?.routeConfig?.path);
      case APP_PATHS.games.case_battle.base:
        return this.#getBattlesFirstLoad();
      case APP_PATHS.games.challenge.base:
        return this.#getBattlesFirstLoad();
      case APP_PATHS.games.classic.base:
        return this.#getClassicFirstLoad();
      default:
        return of(null);
    }
  }

  #getCasesFirstLoad(): Observable<ICaseCollection[]> {
    return this.#casesBackendService.getCategories({ detailed: true });
  }

  #getHiloFirstLoad(
    user?: IUserDetailed | null,
    parentPath?: string,
  ): Observable<{ currentStat: IHiloCurrentGameState; gamesHistory: IHiloHistoryItem[] }> {
    const currentCurrency = parentPath === EWalletType.FREE_COINS ? EWalletType.FREE_COINS : EWalletType.REAL;
    return forkJoin({
      currentStat: this.#hiloBackendService.getState(currentCurrency),
      gamesHistory: this.#hiloBackendService.getHistory(currentCurrency),
    }).pipe(
      map(({ currentStat, gamesHistory }) => ({
        currentStat,
        gamesHistory: gamesHistory.filter((game) => game.id !== currentStat.id),
        userId: user?.id,
      })),
    );
  }

  #getBattlesFirstLoad(): Observable<{ cases: ICaseItemDtoV2[] }> {
    return this.#battleListsBackendService
      .getAllCasesForBattleCreate(DEFAULT_CHALLENGE_CASES_LIST_FILTERS)
      .pipe(map((cases) => ({ cases })));
  }

  #getClassicFirstLoad(): Observable<IClassicStateDto> {
    return this.#classicBackendService.getGameState();
  }
}
